import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { GlobalPositionStrategy } from '@angular/cdk/overlay';
import { booleanAttribute, ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, input, model, Signal, signal, TemplateRef, viewChild, ViewContainerRef, WritableSignal } from '@angular/core';
import { untracked } from '@angular/core/primitives/signals';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api';
import { map, Observable } from 'rxjs';
import { MonacoEditorComponent } from 'src/app/monaco-editor/monaco-editor.component';
import { CustomFieldDto, CustomFieldFormat, DataCurrencyDisplay, DataCurrencyLayout, DataCurrencyNegativeDisplay, DataCurrencySign, DataDateFormat, DataTimeFormat, NumberRoundingMethod } from 'src/app/services/api.services';
import { LocalisationService } from 'src/app/services/localisation.service';
import { GenericAlertButton, GenericAlertDialog } from 'src/app/shared/dialogs/generic-alert/generic-alert.dialog';
import { distinctBy, isNullOrUndefined, rectContains, Trie } from 'src/app/shared/utils';
import { AppCurrencies } from 'src/app/state/app/app.reducer';
import { InsightsQuery, InsightsQuerySyntax } from 'src/insightsQuery';
import { DataDisplayOptions } from '../../dataModel';
import { DataType } from '../../dataModel/DataType';
import { createDateValue, createTimeValue } from '../../dataQuery/expr';
import { DataFormatter } from '../../formatting/DataFormatter';
import { RichTextValue } from '../../RichTextValue';
import { debounced } from '../../utils/debounced';
import { logger } from '../../utils/logger';
import { formControl } from '../../utils/signal';
import { TrgDrag } from '../../utils/TrgDrag.directive';
import { TrgDragDropService } from '../../utils/TrgDragDrop';
import { TrgDrop } from '../../utils/TrgDrop.directive';
import { CustomFieldIssue } from '../customFields/CustomFieldIssue';
import { CustomFields } from '../customFields/CustomFields';
import { CustomFieldSyntaxVisitor } from '../customFields/CustomFieldSyntaxVisitor';
import { VisualCustomField } from '../VisualCustomField';
import { VisualFieldTarget } from '../VisualFieldTarget';

const LOGGER = logger('ExprEditorComponent');

@Component({
  selector: 'app-expr-editor',
  templateUrl: './ExprEditor.component.html',
  styleUrl: './ExprEditor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ExprEditorComponent implements monaco.languages.CompletionItemProvider, monaco.languages.HoverProvider {
  readonly DataType = DataType;
  readonly localizationService = inject(LocalisationService);
  readonly matDialog = inject(MatDialog);
  readonly dragDrop = inject(TrgDragDropService);

  readonly dataFormatter = input<DataFormatter>(null);

  readonly titleInput = input('Edit Calculation');
  readonly title = computed(() => this.openedWith()?.title ?? this.titleInput());

  readonly displayFields = computed(() => !!this.displayMode()?.fields);
  readonly displayFunctions = computed(() => !!this.displayMode()?.functions);

  private readonly destroyRef = inject(DestroyRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly dialog = inject(Dialog);

  readonly disabled = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });

  readonly displayModeInput = input<ExprEditor.DisplayMode>(ExprEditor.DisplayModes.full, { alias: 'displayMode' });
  readonly displayMode = computed(() => this.openedWith()?.displayMode ?? this.displayModeInput());

  readonly fields = input<ReadonlyArray<ExprEditor.FieldItemGroup>>([]);
  readonly functions = input<ReadonlyArray<ExprEditor.FunctionItemGroup>>([]);

  readonly fieldItems = computed(() => new ExprEditor.Items<ExprEditor.FieldItem, ExprEditor.FieldItemGroup>(this.fields()));
  readonly functionItems = computed(() => new ExprEditor.Items<ExprEditor.FunctionItem, ExprEditor.FunctionItemGroup>(this.functions()));

  readonly fieldMap = computed(() => {
    let fieldMap = new Map<string, ExprEditor.FieldItem>();

    this.fieldItems()
      .groups().forEach(g => g
        .children()
        .forEach(i => addItem(i)));

    return fieldMap;

    function addItem(item: ExprEditor.FieldItem) {
      let key = item.target().identifierName.toLowerCase();
      fieldMap.set(key, item);
      item.children().forEach(c => addItem(c));
    }
  });

  readonly functionMap = computed(() => {
    let functionMap = new Map<string, ExprEditor.FunctionItem>();

    this.functionItems().groups().forEach(
      g => g.children().forEach(
        f => functionMap.set(f.shortTitle().toLowerCase(), f)));

    return functionMap;
  });

  readonly fieldResolverInput = input<CustomFieldSyntaxVisitor.FieldResolver>(null, { alias: 'fieldResolver' });
  readonly functionResolverInput = input<CustomFieldSyntaxVisitor.FunctionResolver>(null, { alias: 'functionResolver' });

  readonly fieldResolver = computed<CustomFieldSyntaxVisitor.FieldResolver>(() => {
    let fieldResolver = this.fieldResolverInput();
    if (fieldResolver)
      return fieldResolver;

    let fieldMap = this.fieldMap();
    return identifier => {
      let key = [identifier.table, identifier.field, identifier.childField]
        .filter(x => !!x)
        .map(x => x.toLowerCase())
        .join('.');

      let field = fieldMap.get(key);
      if (!field)
        return null;

      return field.target;
    };
  });

  readonly functionResolver = computed<CustomFieldSyntaxVisitor.FunctionResolver>(() => {
    let functionResolver = this.functionResolverInput();
    if (functionResolver)
      return functionResolver;

    let functionMap = this.functionMap();

    return (name, args) => {
      let func = functionMap.get(name.toLowerCase());
      if (!func)
        return;

      return func.func;
    };
  });


  readonly allowEmptyInput = input(false, { alias: 'allowEmpty' });
  readonly allowEmpty = computed(() => this.openedWith()?.allowEmpty ?? this.allowEmptyInput());

  readonly checkForCyclicDeps = input<ExprEditor.CyclicDependencyCheck>(null);

  readonly nameEditingInput = input<boolean>(false, { alias: 'nameEditing' });
  readonly nameEditing = computed<boolean>(() => this.openedWith()?.nameEditing ?? this.nameEditingInput());

  readonly formatEditingInput = input<boolean>(false, { alias: 'formatEditing' });
  readonly formatEditing = computed<boolean>(() => this.openedWith()?.formatEditing ?? this.formatEditingInput());

  readonly dto = computed<CustomFieldDto>(() => ({
    name: this.name(),

    format: this.format(),

    decimalPrecision: this.decimalPrecision(),
    numberRounding: this.numberRounding(),
    showThousandSeparator: this.showThousandSeparator(),
    significantDigitsOnly: this.significantDigitsOnly(),

    currency: this.currencyDisplay(),

    nullText: this.nullText(),

    dateFormat: this.dateFormat(),
    timeFormat: this.timeFormat(),
  }));

  readonly nameModel = model('', { alias: 'name' });
  readonly name = formControl(this.nameModel, [Validators.required]);
  readonly originalName = signal('');

  readonly usedNames = input<ReadonlyArray<string>>([]);

  get nameValid() { return this.name.valid; };

  readonly format = model<CustomFieldFormat>(CustomFieldFormat.Unknown);
  readonly formatOption = computed(() => this.formatOptions.find(x => x.value == this.format()));
  readonly formatOptions = ExprEditor.Format.items;

  readonly isNumericFormat = computed(() => [
    CustomFieldFormat.Currency,
    CustomFieldFormat.Number,
    CustomFieldFormat.Percentage,
  ].includes(this.format()));
  
  readonly isCurrencyFormat = computed(() => [
    CustomFieldFormat.Currency,
  ].includes(this.format()));

  readonly isDateFormat = computed(() => [
    CustomFieldFormat.DateOnly,
    CustomFieldFormat.DateTime,
  ].includes(this.format()));

  readonly isTimeFormat = computed(() => [
    CustomFieldFormat.TimeOnly,
    CustomFieldFormat.DateTime,
  ].includes(this.format()));

  readonly isTextFormat = computed(() => [
    CustomFieldFormat.Text,
    CustomFieldFormat.Unknown
  ].includes(this.format()));

  readonly decimalPrecision = model<number>(2);
  readonly decimalPrecisionOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  readonly numberRounding = model<NumberRoundingMethod>(NumberRoundingMethod.Truncate);
  readonly numberRoundingOption = computed(() => this.numberRoundingOptions.find(x => x.value == this.numberRounding()));
  readonly numberRoundingOptions = [{
    value: NumberRoundingMethod.Truncate,
    title: 'Truncate',
    helpText: `
      Digits following the decimal point are discarded, e.g.<br/>
      3.14 becomes 3, 0.5 becomes 0.`
  }, {
    value: NumberRoundingMethod.AwayFromZero,
    title: 'Away Form Zero',
    helpText: `
      Rounds to the integer furthest away from zero, e.g.<br/>
      -3.14 becomes -4, 3.14 becomes 4.`
  }, {
    value: NumberRoundingMethod.Nearest,
    title: 'To Nearest Integer',
    helpText: `
      Rounds to the closest integer, e.g.<br/>
      -3.14 becomes -3, 3.14 becomes 3.`
  }, {
    value: NumberRoundingMethod.Floor,
    title: 'Round Down',
    helpText: `
      Rounds to the next lower integer, e.g.<br/>
      -3.14 becomes -4, 3.14 becomes 3.`
  }, {
    value: NumberRoundingMethod.Ceil,
    title: 'Round Up',
    helpText: `
      Rounds to the next higher integer, e.g.<br/>
      -3.14 becomes -3, 3.14 becomes 4.`
  }];
  
  readonly showThousandSeparator = model<boolean>(true);
  readonly showThousandSeparatorOption = computed(() => this.showThousandSeparatorOptions.find(x => x.value == this.showThousandSeparator()));
  readonly showThousandSeparatorOptions = [{
    value: true,
    title: 'Yes',
    helpText: `e.g. 1,000, 512,256.32`
  }, {
    value: false,
    title: 'No',
    helpText: `e.g. 1000, 512256.32`
  }];

  
  readonly significantDigitsOnly = model<boolean>(false);
  readonly significantDigitsOnlyOption = computed(() => this.significantDigitsOnlyOptions.find(x => x.value == this.significantDigitsOnly()));
  readonly significantDigitsOnlyOptions = [{
    value: true,
    title: 'Yes',
    helpText: `
      Numbers will be displayed with fewer digits and a suffix, e.g. 
      1,000,000 would display as 1M, 512,256.32 would display as 512.2K`
  }, {
    value: false,
    title: 'No',
  }];

  readonly currencyDisplay = model<DataCurrencyDisplay>({
    ...DataDisplayOptions.defaults.currency
  });

  readonly currencyDisplayOption = computed(() => this.currencyDisplayOptions.find(x => x.sameAs(this.currencyDisplay())));
  readonly currencyDisplayOptions = [
    new CurrencyDisplayOption({
      sign: DataCurrencySign.Sign,
      layout: DataCurrencyLayout.Accounting,
      negativeDisplay: DataCurrencyNegativeDisplay.WithValue
    }, 'Sign', `e.g. $1.00, -$1.00`),
    
    new CurrencyDisplayOption({
      sign: DataCurrencySign.Code,
      layout: DataCurrencyLayout.Accounting,
      negativeDisplay: DataCurrencyNegativeDisplay.WithValue
    }, 'Code', `e.g. 1.00 USD, -1.00 USD`),
    
    new CurrencyDisplayOption({
      sign: DataCurrencySign.None,
      layout: DataCurrencyLayout.Default,
      negativeDisplay: DataCurrencyNegativeDisplay.WithValue
    }, 'None', `e.g. 1.00, -1.00`),
  ];

  readonly nullText = model<string>('#NULL');

  readonly dateFormat = model<DataDateFormat>(DataDateFormat.Short);
  readonly dateFormatOption = computed(() => this.dateFormatOptions().find(x => x.value == this.dateFormat()));
  readonly dateFormatOptions = computed(() => [{
    value: DataDateFormat.Short,
    title: 'Short',
    helpText: `e.g. ${this.formatExample(DataType.DateOnly, createDateValue(new Date()), {dateFormat: DataDateFormat.Short })}`
  }, {
    value: DataDateFormat.YearPeriod,
    title: 'Year & Period',
    helpText: `e.g. ${this.formatExample(DataType.DateOnly, createDateValue(new Date()), {dateFormat: DataDateFormat.YearPeriod })}`
  }]);

  readonly timeFormat = model<DataTimeFormat>(DataTimeFormat.Full);
  readonly timeFormatOption = computed(() => this.timeFormatOptions().find(x => x.value == this.timeFormat()));
  readonly timeFormatOptions = computed(() => [{
    value: DataTimeFormat.Full,
    title: 'Long',
    helpText: `e.g. ${this.formatExamples(DataType.TimeOnly, [createTimeValue('06:30:00'), createTimeValue('16:20:40')], {timeFormat: DataTimeFormat.Full })}`
  }, {
    value: DataTimeFormat.NoSeconds,
    title: 'Short',
    helpText: `e.g. ${this.formatExamples(DataType.TimeOnly, [createTimeValue('06:30:00'), createTimeValue('16:20:40')], {timeFormat: DataTimeFormat.NoSeconds })}`
  }]);

  private formatExamples(dataType: DataType, values: any[], display: DataDisplayOptions) {
    return values.map(x => this.formatExample(dataType, x, display)).join(', ');
  }

  private formatExample(dataType: DataType, value: any, display: DataDisplayOptions) {
    let formatter = (this.dataFormatter() ?? DataFormatter.createDefault({ currencies: AppCurrencies.fallback }));
    return formatter?.formatForDisplay(value, {
      dataType,
      display
    });
  }


  readonly dataType = computed<DataType>(() => VisualCustomField.dataTypeFrom(this.format()));

  readonly value = model<string>('');

  private readonly updateMonacoFromValue = effect(() => {
    let m = this.monaco();
    if (m) {
      let value = this.value();
      m.setValue(value);
    }
  });

  private setValueFromMonaco() {
    this.value.set(this.monaco()?.getValue());
    this.runValidationDebounced();
  }

  readonly validationResult = signal<ExprEditor.ValidateExprResult>(ExprEditor.ValidateExprResult.valid);

  readonly anyErrors = computed(() => this.validationResult().anyErrors);
  readonly anyWarnings = computed(() => this.validationResult().anyWarnings);
  readonly issues = computed(() => this.validationResult().issues);
  readonly visibleIssues = computed(() => distinctBy(this.issues(), x => x.message));

  readonly valid = computed(() => this.validationResult().valid);
  readonly issuesExpanded = model(false);
  readonly userExpandedIssues = signal(false);

  readonly runValidationDebounced = debounced(() => this.runValidation(), 100, this.destroyRef);

  private runValidation() {
    untracked(() => {
      this.runValidationDebounced.cancel();

      this.validationResult.set(this.validateExpr({
        expr: this.value(),
        allowEmpty: this.allowEmpty(),
        checkForCyclicDeps: this.checkForCyclicDeps(),
      }));
    });
  }


  private readonly collapseIssues = effect(() => {
    if (!this.userExpandedIssues() && !!this.validationResult()?.valid)
      this.issuesExpanded.set(false);
  });

  toggleIssues() {
    let expand = !this.issuesExpanded();
    this.issuesExpanded.set(expand);
    this.userExpandedIssues.set(expand);
  }


  readonly dropping = signal(false);

  onEditorDrop(item: ExprEditor.FieldItem | ExprEditor.FunctionItem) {
    let monaco = this.monaco();
    if (monaco) {
      if (this.dropClosestSpan) {
        let range = this.findRangeForClosestSpan();
        if (range) {
          monaco.replaceText(range, item.monacoSuggestion().completion.insertText);
        }
      }
      else {
        monaco.insertText(item.monacoSuggestion().completion.insertText);
      }
    }

    this.monacoValueChange();

    this.clearDropClosestSpan();

    this.dropping.set(false);
  }

  private findRangeForClosestSpan(): monaco.IRange {
    // finds range of a span in the monaco model
    // the way this works is that it advances position up to the span selected for drop using text find
    // don't know of a way to map span to a range otherwise 

    let model = this.monaco().monaco.getModel();
    let value = model.getValue();

    let spans = this.dropIdentifierSpans;
    let offset = 0;
    let prevOffset = 0;

    for (let i = 0; i < spans.length; ++i) {
      let current = spans[i];

      offset = value.indexOf(current.text, offset);
      if (offset === -1) {
        LOGGER.error(`Failed to find match for span`, current);
        break;
      }

      if (current == this.dropClosestSpan) {
        let start = model.getPositionAt(offset);
        let end   = model.getPositionAt(offset + current.text.length);

        return new monaco.Range(
          start.lineNumber,
          start.column,
          end.lineNumber,
          end.column);
      }
      else {
        offset += current.text.length;
      }
    }

    return null;
  }

  private dropIdentifierSpans: DropSpan[] = [];
  private dropClosestSpan: DropSpan;

  onDragEnterDrop(e: TrgDrop.DropEvent) {
    this.dropping.set(true);
    e.preview.element.classList.toggle('expr-editor-drag-inside-span', true);
  }
  
  onDragExitDrop(e: TrgDrop.DropEvent) {
    this.dropping.set(false);
    e.preview.element.classList.toggle('expr-editor-drag-inside-span', false);
  }

  onDragMove(e: TrgDrag.DragEvent, item: ExprEditor.FieldItem) {
    untracked(() => {
      let target: DropSpan = null;

      if (this.dropping()) {
        let ex = e.mouseEvent.clientX;
        let ey = e.mouseEvent.clientY;

        target = this.dropIdentifierSpans.find(x => x.contains(ex, ey));
        if (!target) {
          let spansByDist = this.dropIdentifierSpans.map(x => ({
            span: x,
            dist: x.distTo(ex, ey)
          })).sort((a, b) => a.dist - b.dist);

          target = spansByDist.length ? spansByDist[0].span : null;
        }
      }

      this.clearDropClosestSpan();

      if (target) {
        this.dropClosestSpan = target;
        this.dropClosestSpan.toggleClassName('expr-edior-drop-span', true);
      }
    });
  }

  readonly droppingChange = effect(() => {
    let [dropping, drag] = [this.dropping(), this.dragDrop.drag()];
    
    untracked(() => {
      this.clearDropClosestSpan();

      if (dropping) {
        this.dropIdentifierSpans = this.getIdentifierSpans();
      }
    });
  });

  private clearDropClosestSpan() {
    this.dropClosestSpan?.toggleClassName('expr-edior-drop-span', false);
    this.dropClosestSpan = null;
  }


  private getIdentifierSpans() {
    let editorNode = this.monaco()?.monaco?.getDomNode();
    if (!editorNode)
      return;

    // HACK: mtk14 is the class generated for 'identifier' tokens
    // Not sure if there's a way to get this dynamically?
    let spansQuery = editorNode.querySelectorAll('.mtk14');
    let spans: HTMLSpanElement[] = [];

    spansQuery.forEach(x => spans.push(<HTMLSpanElement>x));
    spans.sort((a, b) => pointDist(0, 0, a.clientLeft, a.clientTop) - pointDist(0, 0, b.clientLeft, b.clientTop));

    let array: DropSpan[] = [];
    let dropSpan: DropSpan;

    for (let i = 0; i < spans.length; ++i) {
      let span = spans[i];
      let text = span.textContent;
      if (text[0] == '[') {
        dropSpan = new DropSpan();
      }

      if (!dropSpan)
        continue;

      dropSpan.spans.push(span);

      if (text[text.length - 1] == ']') {
        array.push(dropSpan);
        dropSpan = null;
      }
    }

    array.forEach(x => x.init());

    return array;
  }


  readonly fieldFilterPattern = model<RegExp[]>(null);
  readonly filteringFields = computed(() => !!this.fieldFilterPattern());
  readonly hideUnmatchedFields = model<boolean>(true);

  readonly functionFilterPattern = model<RegExp[]>(null);
  readonly filteringFunctions = computed(() => !!this.functionFilterPattern());
  readonly hideUnmatchedFunctions = model<boolean>(true);

  onItemFilterMatched(item: ExprEditor.FieldItem, matched: boolean) {
    item.filterMatched.set(matched);

    if (matched) {
      item.group.filterMatched.set(true);
      item.group.expand();
    }
  }

  readonly anyFieldExpanded = computed(() => {
    return this.fields().some(g => !g.collapsed());
  });

  toggleAllFields() {
    if (this.anyFieldExpanded()) {
      this.fields().forEach(ig => ig.collapse());
    }
    else {
      this.fields().forEach(ig => ig.expand());
    }
  }


  readonly openedWith = signal<ExprEditor.OpenInput>(null);
  readonly opened = model(false);

  private readonly __openedChange = effect(() => {
    if (this.opened())
      this.open();
    else
      this.close();
  });


  private dialogContent = viewChild('dialogContent', { read: TemplateRef });
  private dialogRef: DialogRef<any>;

  open(input?: ExprEditor.OpenInput, restoreFocus?: HTMLElement): Observable<ExprEditor.DialogResult> {
    if (this.opened())
      return;

    this.userExpandedIssues.set(false);

    this.validationResult.set(ExprEditor.ValidateExprResult.valid);

    this.openedWith.set(input);

    if (!isNullOrUndefined(input?.expr))
      this.value.set(input.expr);

    if (!isNullOrUndefined(input?.name))
      this.name.set(input.name);

    let dto: CustomFieldDto = {
      ...input?.dto
    };

    if (!isNullOrUndefined(input?.dto)) {
      this.format.set(input.dto.format ?? CustomFieldFormat.Number);

      this.decimalPrecision.set(input.dto.decimalPrecision ?? 2);
      this.numberRounding.set(input.dto.numberRounding ?? NumberRoundingMethod.Truncate);
      this.showThousandSeparator.set(!!input.dto.showThousandSeparator);
      this.significantDigitsOnly.set(!!input.dto.significantDigitsOnly);

      this.currencyDisplay.set(input.dto.currency);

      this.nullText.set(input.dto.nullText);

      this.dateFormat.set(input.dto.dateFormat);
      this.timeFormat.set(input.dto.timeFormat);
    }

    let expr = input?.expr;
    let name = input?.name;

    if (!input?.skipInitialValidation)
      this.runValidation();

    this.opened.set(true);

    this.dialogRef = this.dialog.open(this.dialogContent(), {
      autoFocus: '.app-expr-editor-monaco-editor',
      restoreFocus,
      closeOnNavigation: true,

      panelClass: 'app-expr-editor-dialog-panel',

      hasBackdrop: true,
      backdropClass: 'app-expr-editor-backdrop',

      width: 'auto',
      minWidth: '600px',
      maxWidth: '100vw',

      height: 'auto',
      minHeight: '740px',
      maxHeight: '100vh',

      positionStrategy: new GlobalPositionStrategy()
        .centerHorizontally()
        .top('20px')
    });

    this.dialogRef.closed.subscribe(() => {
      this.opened.set(false);
      this.dialogRef = null;
    });

    return this.dialogRef.closed.pipe(
      map(r => {
        if (r) {
          expr = this.monaco()?.getValue() ?? this.value();
          name = this.name();
          dto = { ...this.dto(), name, sourceText: expr };
        }

        return {
          editor: this,
          save: r,

          expr,
          name,
          dto
        };
      })
    );
  }

  close(save?: boolean) {
    if (!this.opened())
      return;

    this.opened.set(false);

    this.dialogRef?.close(save);
    this.dialogRef = null;
  }

  validateExpr(input: ExprEditor.ValidateExprInput): ExprEditor.ValidateExprResult {
    let issues = this.validate(input);
    let anyWarnings = issues.some(i => i.type == ExprEditor.IssueType.Warning);
    let anyErrors = issues.some(i => i.type == ExprEditor.IssueType.Error);

    return {
      valid: !anyErrors,
      issues,
      anyWarnings,
      anyErrors,
    };
  }

  private validate(input: ExprEditor.ValidateExprInput): ExprEditor.Issue[] {
    if (!input.expr?.trim().length) {
      if (!input.allowEmpty) {
        return [{
          type: ExprEditor.IssueType.Error,
          message: 'The expression must be provided',
          textLocation: InsightsQuery.TextLocation.first
        }];
      }

      return [];
    }

    let issues: ExprEditor.Issue[] = [];

    let parseErrors: InsightsQuery.ParseError[] = [];
    let syntax: InsightsQuerySyntax;

    try {
      syntax = InsightsQuery.parse(input.expr, parseErrors);
    }
    catch (err) {
      if (err instanceof InsightsQuery.ParseError) 
        parseErrors.push(err);
    }

    if (syntax) {
      let checkForCyclicDeps = input.checkForCyclicDeps;

      let visitResult = CustomFieldSyntaxVisitor.visit(syntax, {
        fieldResolver: this.fieldResolver(),
        functionResolver: this.functionResolver(),

        skipCircularDependencyCheck: !checkForCyclicDeps,
        field: checkForCyclicDeps?.identifier,

        targetDataType: this.dataType()
      });

      for (let i of visitResult.issues) {
        issues.push({
          type: (i.type == CustomFieldIssue.Type.Error) 
            ? ExprEditor.IssueType.Error
            : ExprEditor.IssueType.Warning,
          message: i.message,
          textLocation: i.location,
        });
      }
    }

    issues.push(...parseErrors.map(e => ({
      type: ExprEditor.IssueType.Error,
      message: e.message,
      textLocation: e.location,
    })));

    return issues;
  }


  cancelClick() {
    this.close(false);
  }


  saveClick() {
    let name = this.name()?.toLowerCase();
    if (name && name != this.originalName()?.toLowerCase()) {
      if (this.usedNames().some(x => x.toLowerCase() == name)) {
        this.nameConflict();
        return;
      }
    }

    if (!this.nameValid()) {
      this.invalidName();
      return;
    }

    this.runValidation();

    if (!this.allowEmpty() && !this.value()?.trim().length) {
      this.emptyExpr();
      return;
    }

    this.close(true);
  }

  invalidName() {
    this.name.markAsTouched({ onlySelf: true, emitEvent: true });

    GenericAlertDialog.warn(
      this.matDialog,
      GenericAlertDialog,
      {
        title: 'Alert',
        titleIcon: 'fas fa-exclamation-triangle',
        content: "Please give this Calculated Field a Name",
        buttons: [GenericAlertButton.close],
      }
    );
  }

  emptyExpr() {
    GenericAlertDialog.warn(
      this.matDialog,
      GenericAlertDialog,
      {
        title: 'Alert',
        titleIcon: 'fas fa-exclamation-triangle',
        content: 'Please input an expression.',
        buttons: [GenericAlertButton.close],
      }
    );
  }

  nameConflict() {
    GenericAlertDialog.warn(
      this.matDialog,
      GenericAlertDialog,
      {
        title: 'Alert',
        titleIcon: 'fas fa-exclamation-triangle',
        content: `<p>A calculated field with this name already exists</p><p>Please enter a unique name.</p>`,
        buttons: [GenericAlertButton.close],
      }
    );
  }

  fieldDblClick(e: Event, field: ExprEditor.FieldItem) {
    e.stopPropagation();

    this.monaco()?.insertText(field.monacoSuggestion().completion.insertText);
    this.monacoValueChange();
  }

  toolbarInsertClick(text: string) {
    this.monaco()?.insertText(text);
    this.monacoValueChange();
  }

  undoClick() {
    let editor = this.monaco()?.monaco;
    if (!editor)
      return;

    editor.trigger(null, 'undo', null);
    this.monacoValueChange();
  }

  redoClick() {
    let editor = this.monaco()?.monaco;
    if (!editor)
      return;

    editor.trigger(null, 'redo', null);
    this.monacoValueChange();
  }

  cutClick() {
    let editor = this.monaco()?.monaco;
    if (!editor)
      return;

    editor.focus();
    document.execCommand('cut');
    this.monacoValueChange();
  }

  copyClick() {
    let editor = this.monaco()?.monaco;
    if (!editor)
      return;

    editor.focus();
    document.execCommand('copy');
  }

  async pasteClick() {
    let editor = this.monaco()?.monaco;
    if (!editor)
      return;

    editor.focus();

    try {
      let textToPaste = await navigator.clipboard.readText();
      this.monaco().insertText(textToPaste);
    } catch (error) {
      document.execCommand('paste');
    }

    this.monacoValueChange();
  }

  clearAllClick() {
    this.monaco()?.clear();
    this.monacoValueChange();
  }


  monacoValueChange() {
    if (this.destroyed)
      return;

    let m = this.monaco();
    if (!m?.monaco)
      return;

    this.setValueFromMonaco();
  }

  destroyed = false;

  ngOnDestroy() {
    this.destroyed = true;

    this.dialogRef?.close();
  }

  ngAfterViewInit() {
    this.monaco()?.resize();
  }


  fieldSuggestions = computed<MonacoSuggestions<ExprEditor.FieldItem>>(() => {
    return MonacoSuggestions.create(
      this.fields().flatMap(f => f.children()).flatMap(f => [f, ...f.children()]),
      f => f.identifier,
      f => f.monacoSuggestion(),
      /[\.]/gi);
  });

  functionSuggestions = computed<MonacoSuggestions<ExprEditor.FunctionItem>>(() => {
    return MonacoSuggestions.create(
      this.functions().flatMap(f => f.children()).flatMap(f => [f, ...f.children()]),
      f => f.shortTitle(),
      f => f.monacoSuggestion(),
      /[\s\(\)]/gi);
  });

  triggerCharacters?: string[];

  provideCompletionItems(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    context: monaco.languages.CompletionContext,
    token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.CompletionList> {
    let textUntilPosition = model.getValueInRange({
      startLineNumber: position.lineNumber,
      startColumn: 1,

      endLineNumber: position.lineNumber,
      endColumn: position.column
    });

    let completionItems: monaco.languages.CompletionItem[] = [];

    if (textUntilPosition[textUntilPosition.length - 1] == '[') {
      let range = expandRangeLeft(position, 1);
      let closingBracketRange = findRangeToClosingBracket(model, range, ']');

      getCompletionItemsFromSuggestions(this.fieldSuggestions().items, range, closingBracketRange);
    }
    else {
      // Identifier match
      let identifierMatch = textUntilPosition.match(/\[([^\]]+?)(\])?$/);
      if (identifierMatch) {
        let range = expandRangeLeft(position, identifierMatch[0].length);
        let closingBracketRange = findRangeToClosingBracket(model, range, ']');

        let identifierStart = identifierMatch[1].trim().toLowerCase();

        let hasPeriod = identifierStart.indexOf('.') !== -1;
        let search = hasPeriod ? identifierStart.split('.') : identifierStart;

        let matches = this.fieldSuggestions().trie.searchDistinct(search, x => x.item, 10);
        
        getCompletionItemsFromSuggestions(matches, range, closingBracketRange);
      }
      else {
        let word = model.getWordAtPosition(position);

        if (word) {
          // Function match
          let functionMatch = word.word.match(/[a-zA-Z_][a-zA-Z_0-9]*?(\s*\()?$/);
          if (functionMatch) {
            let range = {
              startLineNumber: position.lineNumber,
              startColumn: word.startColumn,

              endLineNumber: position.lineNumber,
              endColumn: word.endColumn,
            };

            let functionStart = word.word.toLowerCase();

            let functionMatches = this.functionSuggestions().trie.searchDistinct(functionStart, x => x.item, 10);

            getCompletionItemsFromSuggestions(functionMatches, range);
          }
        }
        else {
          let range = expandRangeLeft(position, 1);

          if (/([^\s])?\s*$/.exec(textUntilPosition)[1] != '(')
            getCompletionItemsFromSuggestions(this.functionSuggestions().items, range);

          getCompletionItemsFromSuggestions(this.fieldSuggestions().items, range);
        }
      }
    }

    return {
      suggestions: completionItems,
      incomplete: false,
    };

    function getCompletionItemsFromSuggestions<T>(
      suggestions: ReadonlyArray<MonacoSuggestionItem<T>>,
      range: monaco.IRange,
      closingBracketRange?: monaco.IRange
    ) {
      for (let { completion } of suggestions) {
        let additionalTextEdits: monaco.editor.ISingleEditOperation[] = null;

        if (closingBracketRange) {
          additionalTextEdits = [
            {
              range: closingBracketRange,
              text: ''
            }
          ];
        }

        completionItems.push({
          ...completion,
          additionalTextEdits,
          range,
        });
      }
    }
  }

  provideHover(
    model: monaco.editor.ITextModel,
    position: monaco.Position,
    token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.Hover> {
    return null;
  }


  readonly monaco = viewChild('monacoEditor', { read: MonacoEditorComponent });

  readonly monacoConfig: monaco.editor.IStandaloneEditorConstructionOptions = {
    ...MonacoEditorComponent.defaultConfig,

    extraEditorClassName: 'monaco-editor__query-editor',

    language: 'insightsqueryexpression',
    theme: 'light',

    lineNumbers: 'off',
    lineNumbersMinChars: 2,

    padding: {
      top: 4,
      bottom: 2
    },

    renderWhitespace: 'none',
    guides: {
      indentation: false
    },

    folding: false,
    glyphMargin: false,
    renderLineHighlight: 'none',

    overviewRulerBorder: false,

    scrollbar: {
      horizontal: 'auto',
      vertical: 'auto',
      useShadows: false
    },

    contextmenu: true,

    minimap: {
      enabled: false
    },

    suggestFontSize: 12,
    suggestLineHeight: 22,
    suggestSelection: 'first',
    suggestOnTriggerCharacters: true,
    suggest: {
      filterGraceful: true,
      shareSuggestSelections: false,
      localityBonus: false,
      previewMode: 'prefix',
      snippetsPreventQuickSuggestions: true,

      showInlineDetails: true,
      matchOnWordStartOnly: false,

      preview: true,
      showIcons: false,
    },

    inlineSuggest: {
      enabled: true,
      keepOnBlur: false,
    },

    wordBasedSuggestions: false,
    quickSuggestions: true,
    acceptSuggestionOnEnter: 'smart',
    snippetSuggestions: 'inline',

    fontSize: 15,
    lineHeight: 22,
    fontFamily: 'Consolas, Menlo, Courier, monoscape',

    lineDecorationsWidth: 5,

    hover: {
      enabled: false,
    }
  };
}

function findRangeToClosingBracket(model: monaco.editor.ITextModel, range: monaco.IRange, bracket: string) {
  let position: monaco.IPosition = {
    lineNumber: range.endLineNumber,
    column: range.endColumn
  };

  let match = model.findNextMatch(bracket, position, false, false, null, false);
  if (match
    && match.range.startLineNumber == range.endLineNumber
    && match.range.startColumn >= range.endColumn) {
    return {
      ...match.range,
      startLineNumber: range.endLineNumber,
      startColumn: range.endColumn
    };
  }

  return null;
}

function expandRangeLeft(position: any, n: number) {
  return {
    startLineNumber: position.lineNumber,
    startColumn: position.column - n,

    endLineNumber: position.lineNumber,
    endColumn: position.column,
  };
}

interface MonacoSuggestionItem<T> {
  readonly item: T;
  readonly completion: monaco.languages.CompletionItem;
};

interface MonacoSuggestions<T> {
  readonly items: ReadonlyArray<MonacoSuggestionItem<T>>;
  readonly trie: Trie<MonacoSuggestionItem<T>>;
}

namespace MonacoSuggestions {
  export function create<T>(
    from: ReadonlyArray<T>,
    token: (item: T) => string,
    toItem: (item: T) => MonacoSuggestionItem<T>,
    tokenSeparator?: RegExp): MonacoSuggestions<T> {
    let items: MonacoSuggestionItem<T>[] = [];
    let trie = new Trie<MonacoSuggestionItem<T>>({
      splitOnRegEx: (tokenSeparator ?? /[\s\.\(\)]/gi),
      ignoreCase: true,
    });

    for (let x of from) {
      let item = toItem(x);
      items.push(item);

      let tok = token(x);
      if (tok[0] == '[' && tok[tok.length - 1] == ']')
        tok = tok.substring(1, tok.length - 1);

      trie.add(tok, item);
    }

    return {
      items,
      trie
    };
  }
}

export namespace ExprEditor {
  export interface DialogResult {
    readonly editor: ExprEditorComponent;
    readonly save: boolean;

    readonly name?: string;
    readonly expr?: string;
    readonly dto?: CustomFieldDto;
  }


  export interface DisplayMode {
    fields: boolean;
    functions: boolean;
  }

  export const DisplayModes = {
    full: {
      fields: true,
      functions: true
    },
    noFunctions: {
      fields: true,
      functions: false
    },
    noFields: {
      fields: false,
      functions: true
    },
    queryOnly: {
      fields: false,
      functions: false
    },
  };

  export interface OpenInput {
    readonly title?: string;
    readonly displayMode?: DisplayMode;

    readonly expr?: string;
    readonly allowEmpty?: boolean;

    readonly nameEditing?: boolean; 
    readonly formatEditing?: boolean; 
    readonly name?: string;
    readonly dto?: CustomFieldDto;

    readonly skipInitialValidation?: boolean;
  }

  export interface ValidateExprInput {
    readonly expr?: string;
    readonly allowEmpty?: boolean;
    readonly checkForCyclicDeps?: CyclicDependencyCheck;
  }

  export interface ValidateExprResult {
    readonly valid: boolean;
    readonly issues: ReadonlyArray<Issue>;
    readonly anyWarnings: boolean;
    readonly anyErrors: boolean;
  }

  export namespace ValidateExprResult {
    export const valid: ValidateExprResult = {
      anyErrors: false,
      anyWarnings: false,
      issues: [],
      valid: true
    };
  }

  export interface CyclicDependencyCheck {
    readonly identifier: string;
  }


  export class Items<T extends ItemBase<T, G>, G extends GroupBase<T, G>> {
    readonly groups: WritableSignal<ReadonlyArray<G>>;

    constructor(g: ReadonlyArray<G>) {
      let groups = [...g];
      this._sortGroups(groups);
      this.groups = signal(groups);
    }

    sortGroups() {
      this.groups.update(l => {
        let newList = [...l];
        this._sortGroups(newList);
        return newList;
      });
    }

    _sortGroups(groups: G[]) {
      groups.sort((a, b) => {
        let ap = a.pinned();
        let bp = b.pinned();
        if (ap != bp) {
          if (ap)
            return -1;
          else if (bp)
            return +1;
        }

        return a.baseDisplayOrder - b.baseDisplayOrder;
      });
    }


    toggleCollapsed(item: ItemBase<T, G>) {
      item.toggleCollapsed();
    }

    togglePinned(item: ItemBase<T, G>) {
      item.togglePinned();
      this.sortGroups();
    }
  }

  export abstract class ItemBase<T extends ItemBase<T, G>, G extends GroupBase<T, G>> {
    constructor(
      public readonly group?: G,
      public readonly parent?: T) { }

    baseDisplayOrder: number;
    icon: Signal<string>;
    shortTitle: Signal<string>;
    longTitle: Signal<string>;
    listTitle: Signal<string>;

    readonly children = signal<ReadonlyArray<T>>([]);

    sortChildren() {
      this.children.update(l => {
        let newList = [...l];
        this._sortItems(newList);
        return newList;
      });
    }

    _sortItems(items: T[]) {
      items.sort((a, b) => {
        let ap = a.pinned();
        let bp = b.pinned();
        if (ap != bp) {
          if (ap)
            return -1;
          else if (bp)
            return +1;
        }

        return a.baseDisplayOrder - b.baseDisplayOrder;
      });
    }


    readonly collapsed = signal(true);

    collapse(propagate = true) {
      this.collapsed.set(true);

      if (propagate)
        this.children().forEach(c => c.collapse());
    }

    expand(bubble = true) {
      this.collapsed.set(false);

      if (bubble) {
        this.parent?.expand(bubble);
      }
    }

    toggleCollapsed() {
      this.collapsed.update(x => !x);
    }


    // Has this group been matched with the filter?
    readonly filterMatched = signal(false);


    readonly pinned = signal<boolean>(false);

    togglePinned() {
      this.pinned.update(x => !x);

      this.group?.sortChildren();
    }
  }

  export abstract class GroupBase<T extends ItemBase<T, G>, G extends GroupBase<T, G>> extends ItemBase<T, G> {
    // Number of visible descendants.
    // A descendant is visible when it is direct child of the group.
    // Additionally, the children of the direct child are counted when the direct child is expanded.
    readonly visibleDescendantCount = computed(() =>
      this.children().reduce((n, c) => {
        let collapsed = c.collapsed();
        let children = c.children();

        n++;

        if (!collapsed)
          n += children.length;

        return n;
      }, 0));

    // Number of visible children, not counting descendants.
    readonly filterMatchedVisibleChildrenCount = computed(() =>
      this.children().reduce((n, c) => n + (c.filterMatched() ? 1 : 0), 0)
    );

    // Similar to visibleDescendantCount, but only considers items matched by filter.
    // Matching is done by VisualEditorFilterListComponent
    readonly filterMatchedVisibleDescendantCount = computed(() => {
      return this.children().reduce((n, c) => {
        let collapsed = c.collapsed();
        let children = c.children();
        let filterMatched = c.filterMatched();

        if (filterMatched) {
          n++;

          if (!collapsed)
            n += children.length;
        }

        return n;
      }, 0);
    });
  }

  export class FieldItemGroup extends GroupBase<FieldItem, FieldItemGroup> {
    listTitle: Signal<string>;
  }

  export class FieldItem extends GroupBase<FieldItem, FieldItemGroup> {
    constructor(
      public readonly group: FieldItemGroup,
      public readonly parent?: FieldItem) { super(); }

    listTitle: Signal<string>;
    target: Signal<VisualFieldTarget>;

    get dataType() { return this.target().dataType; }
    get isAggregated() { return this.target().isAggregated; }
    get identifier() { return this.target().identifierExpr; }
    get isValid() { return this.target().isValid; }

    readonly monacoSuggestion = computed<MonacoSuggestionItem<FieldItem>>(() => ({
      item: this,

      completion: {
        range: null,

        kind: this.isAggregated
          ? monaco.languages.CompletionItemKind.Property
          : monaco.languages.CompletionItemKind.Field,

        label: this.identifier,
        title: this.identifier,
        //detail: this.longTitle(),
        //documentation: this.longTitle(),

        insertText: this.identifier,
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,

        sortText: `1.${this.longTitle()}`,
      },
    }));
  }

  export class FunctionItemGroup extends GroupBase<FunctionItem, FunctionItemGroup> {
    constructor() { super(null, null); }
  }

  export class FunctionItem extends ItemBase<FunctionItem, FunctionItemGroup> {
    readonly argumentListText: Signal<string>;

    constructor(
      public readonly group: FunctionItemGroup,
      public readonly func: CustomFields.Function,
      displayOrder: number) {
      super(group, null);

      this.baseDisplayOrder = displayOrder;
      this.icon = signal('');
      this.shortTitle = signal(func.name);
      this.argumentListText = signal(`(${this.func.arguments?.map(a => `[${a.name}]`).join(', ') ?? ''})`);
      this.longTitle = computed(() => `${this.shortTitle()}${this.argumentListText()}`);
    }

    readonly description = signal<RichTextValue>(this.func.descriptionValue);
    readonly descriptionHTML = computed(() => this.description()?.html);

    readonly monacoSuggestion = computed<MonacoSuggestionItem<FunctionItem>>(() => {
      let longTitle = this.longTitle();

      return {
        item: this,
        completion: {
          range: null,

          kind: monaco.languages.CompletionItemKind.Method,

          label: longTitle,
          title: longTitle,
          documentation: {
            value: this.description().content,
            isTrusted: true
          },

          insertText: longTitle,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,

          sortText: `0.${this.shortTitle()}`,
        },
      };
    });
  }


  export enum IssueType {
    Info,
    Warning,
    Error
  }

  export interface Issue {
    readonly type: IssueType;
    readonly message: string;
    readonly textLocation: InsightsQuery.TextLocation;
  }


  export class Format {
    constructor(
      public readonly icon: string,
      public readonly title: string,
      public readonly dataType: DataType,
      public readonly value: CustomFieldFormat) { }
  }

  export namespace Format {
    export const items: ReadonlyArray<Format> = [
      new Format(
        DataType.Unknown.icon,
        'Any',
        DataType.Unknown,
        CustomFieldFormat.Unknown),

      new Format(
        DataType.Number.icon,
        'Numeric',
        DataType.Number,
        CustomFieldFormat.Number),

      new Format(
        'fas fa-dollar-sign',
        'Currency',
        DataType.Decimal,
        CustomFieldFormat.Currency),

      new Format(
        'fas fa-percent',
        'Percentage',
        DataType.Number,
        CustomFieldFormat.Percentage),

      new Format(
        DataType.DateOnly.icon,
        'Date',
        DataType.DateOnly,
        CustomFieldFormat.DateOnly),

      new Format(
        DataType.DateTime.icon,
        'Date & Time',
        DataType.DateTime,
        CustomFieldFormat.DateTime),

      new Format(
        DataType.TimeOnly.icon,
        'Time Only',
        DataType.TimeOnly,
        CustomFieldFormat.TimeOnly),

      new Format(
        DataType.Text.icon,
        'Text',
        DataType.Text,
        CustomFieldFormat.Text),
    ];
  }
}

export class ExprEditorProvider {
  constructor() { }

  public readonly value = signal<ExprEditorComponent>(null);
}


let nextCurrencyDisplayOptionId = 1;

class CurrencyDisplayOption {
  readonly id = nextCurrencyDisplayOptionId++;

  constructor(
    readonly value: DataCurrencyDisplay,
    readonly title: string,
    readonly helpText: string
  ) { }

  sameAs(options: DataCurrencyDisplay) {
    return options === this.value || (
           options.sign            == this.value.sign &&
           options.layout          == this.value.layout &&
           options.negativeDisplay == this.value.negativeDisplay);
  }
}

class DropSpan {
  spans: HTMLSpanElement[] = [];
  rects: DOMRect[];
  text: string;

  constructor() { }

  init() {
    this.rects = this.spans.map(x => x.getBoundingClientRect());

    let text = [];

    let parent = this.spans[0].parentElement.parentElement;

    for (let span of this.spans) {
      let thisParent = span.parentElement.parentElement;
      if (thisParent != parent) {

        let p = parent;

        do {
          text.push('\r\n');
          p = <HTMLElement>p.nextElementSibling;
        }
        while (thisParent != p && p);

        parent = thisParent;
      }

      text.push(span.textContent.replace(/ /g, ' '));
    }

    this.text = text.join('');
  }

  contains(x: number, y: number) {
    return this.rects.some(r => rectContains(r, x, y));
  }

  distTo(x: number, y: number) {
    return this.rects.map(rect => {
      let cx = rect.x + rect.width / 2;
      let cy = rect.y + rect.height / 2;

      return Math.min(
        pointDist(cx, cy, x, y),
        pointDist(rect.left, rect.top, x, y),
        pointDist(rect.left, rect.bottom, x, y),
        pointDist(rect.right, rect.top, x, y),
        pointDist(rect.right, rect.bottom, x, y));
    }).reduce((a, b) => Math.min(a, b), 9999);
  }

  toggleClassName(className: string, enabled: boolean) {
    this.spans.forEach(x => x.classList.toggle(className, enabled));
  }
}

function pointDist(ax: number, ay: number, bx: number, by: number) {
  let distX = ax - bx;
  let distY = ay - by;
  return Math.sqrt(distX * distX + distY * distY);
}
